
import React, { useState } from 'react';

interface ClimateStepProps {
  t: any;
  isRTL: boolean;
  language: string;
  onBack: () => void;
  onHome: () => void;
  onContinue: (clim: string[]) => void;
}

const ClimateStep: React.FC<ClimateStepProps> = ({ t, isRTL, language, onBack, onHome, onContinue }) => {
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const [isFlexible, setIsFlexible] = useState(false);

  const toggleOption = (id: string) => {
    setIsFlexible(false);
    const newSelected = new Set(selectedOptions);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else if (newSelected.size < 2) {
      newSelected.add(id);
    }
    setSelectedOptions(newSelected);
  };

  const handleFlexible = () => {
    setSelectedOptions(new Set());
    setIsFlexible(true);
  };

  const options = [
    {
      id: 'spring',
      title: t.options.spring.title,
      desc: t.options.spring.desc,
      icon: (
        <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14.5 5.5a2.5 2.5 0 0 0-5 0v10a4 4 0 1 0 5 0v-10Z"></path>
          <path d="M12 13v3"></path>
        </svg>
      )
    },
    {
      id: 'warm',
      title: t.options.warm.title,
      desc: t.options.warm.desc,
      icon: (
        <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.5 9.5a3.5 3.5 0 1 1-3.5-3.5"></path>
          <path d="M12 10.5a4.5 4.5 0 0 1 4.2 3A3.5 3.5 0 0 1 16 20H7a3.5 3.5 0 0 1-.5-7 4.5 4.5 0 0 1 5.5-2.5"></path>
          <path d="M17.5 4v2 M21.5 8h-2 M20.5 5l-1.5 1.5 M14.5 5l1.5 1.5"></path>
        </svg>
      )
    },
    {
      id: 'dry',
      title: t.options.dry.title,
      desc: t.options.dry.desc,
      icon: (
        <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19h16"></path>
          <path d="M7 19a5 5 0 0 1 10 0"></path>
          <path d="M12 11v-3 M8 12.5l-2-2 M16 12.5l2-2 M6 19h-2 M18 19h2"></path>
        </svg>
      )
    },
    {
      id: 'seasons',
      title: t.options.seasons.title,
      desc: t.options.seasons.desc,
      icon: (
        <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3v18"></path>
          <path d="M4.2 7.5l15.6 9"></path>
          <path d="M19.8 7.5l-15.6 9"></path>
          <path d="M12 3l-2 3 M12 3l2 3 M12 21l-2-3 M12 21l2-3"></path>
        </svg>
      )
    },
    {
      id: 'highland',
      title: t.options.highland.title,
      desc: t.options.highland.desc,
      icon: (
        <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18.5 7.5l-8 12h-8l5.5-8.5"></path>
          <path d="M14.5 13.5l3.5-5.5 4.5 7.5h-4"></path>
        </svg>
      )
    },
    {
      id: 'desert',
      title: t.options.desert.title,
      desc: t.options.desert.desc,
      icon: (
        <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4.5"></circle>
          <path d="M12 2v2.5 M12 19.5V22 M4.9 4.9l1.8 1.8 M17.3 17.3l1.8 1.8"></path>
        </svg>
      )
    }
  ];

  const canContinue = selectedOptions.size > 0 || isFlexible;

  return (
    <div className={`relative flex flex-col h-screen w-full max-w-2xl mx-auto font-display ${isRTL ? 'text-right' : 'text-left'}`}>
      <header className={`relative w-full pt-6 md:pt-10 px-8 md:px-12 pb-2 md:pb-4 flex items-center justify-between z-20`}>
        <button 
          onClick={onHome}
          className={`flex items-center gap-2.5 transition-opacity hover:opacity-80 active:scale-95 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
        >
          <span className="material-symbols-outlined text-[#0066FF] text-[26px] md:text-[30px] font-bold">flight_takeoff</span>
          <h2 className="text-slate-dark tracking-tighter text-[20px] md:text-[22px] font-black leading-none">Settlo</h2>
        </button>
        <button onClick={onBack} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100 text-slate-soft hover:text-primary transition-all active:scale-95 group">
          <span className="text-[12px] font-bold uppercase tracking-[0.1em]">{language === 'zh' ? '返回' : 'Back'}</span>
          <span className={`material-symbols-outlined text-[18px] ${isRTL ? '' : 'rotate-180'}`}>arrow_forward</span>
        </button>
      </header>

      <main className="px-8 md:px-12 pb-2 flex flex-col flex-grow pt-4 md:pt-8 overflow-y-auto no-scrollbar">
        <div className={`mb-6 md:mb-10 flex flex-col ${isRTL ? 'items-end' : 'items-start'}`}>
          <div className="text-slate-soft/60 text-[11px] font-black tracking-[0.3em] uppercase mb-3">
            {t.step}
          </div>
          <h1 className="text-[28px] md:text-[42px] font-extrabold tracking-tight leading-[1.1] text-slate-dark mb-4">
            {t.title}
          </h1>
        </div>

        <section className="flex-grow space-y-4 md:space-y-6">
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {options.map((opt) => (
              <button
                key={opt.id}
                onClick={() => toggleOption(opt.id)}
                className={`group relative flex flex-col items-start gap-1.5 p-4 md:p-6 rounded-stripe border transition-all duration-300 transform active:scale-[0.98] ${
                  selectedOptions.has(opt.id) ? 'bg-primary/5 border-primary shadow-lg shadow-primary/10' : 'bg-white border-[#E6E6E8] shadow-stripe-card hover:border-primary/40'
                }`}
              >
                <div className="flex items-center justify-between w-full mb-3 md:mb-4">
                  <div className={`flex items-center justify-center w-9 h-9 md:w-11 md:h-11 rounded-lg transition-colors duration-300 ${selectedOptions.has(opt.id) ? 'bg-primary text-white' : 'bg-slate-50 text-slate-soft'}`}>{opt.icon}</div>
                  {selectedOptions.has(opt.id) && <span className="material-symbols-outlined text-primary text-[20px] md:text-[24px]">check_circle</span>}
                </div>
                <span className={`text-[15px] md:text-[16px] font-bold tracking-tight ${selectedOptions.has(opt.id) ? 'text-primary' : 'text-slate-dark'}`}>{opt.title}</span>
                <span className={`text-[12px] md:text-[14px] leading-tight ${selectedOptions.has(opt.id) ? 'text-primary/70' : 'text-slate-soft'}`}>{opt.desc}</span>
              </button>
            ))}
          </div>

          <button onClick={handleFlexible} className={`w-full group relative flex items-center justify-between p-4 md:p-6 rounded-stripe border transition-all duration-300 transform active:scale-[0.99] ${isFlexible ? 'bg-primary/5 border-primary shadow-lg shadow-primary/10' : 'bg-white border-[#E6E6E8] shadow-stripe-card'}`}>
            <div className="flex items-center gap-4">
              <div className={`flex items-center justify-center w-9 h-9 md:w-11 md:h-11 rounded-lg transition-colors ${isFlexible ? 'bg-primary text-white' : 'bg-slate-50 text-slate-soft'}`}><span className="material-symbols-outlined text-[20px] md:text-[24px]">shuffle</span></div>
              <span className={`text-[15px] md:text-[17px] font-bold tracking-tight ${isFlexible ? 'text-primary' : 'text-slate-dark'}`}>{t.flexible}</span>
            </div>
            {isFlexible && <span className="material-symbols-outlined text-primary text-[20px] md:text-[24px]">check_circle</span>}
          </button>
        </section>
      </main>

      <footer className="w-full px-8 md:px-12 pb-8 md:pb-12 pt-4 shrink-0">
        <button 
          disabled={!canContinue}
          onClick={() => canContinue && onContinue(Array.from(selectedOptions))}
          className={`group w-full relative flex items-center justify-center gap-2.5 font-black text-[17px] py-4 md:py-5 rounded-stripe shadow-stripe-button transition-all duration-300 active:scale-[0.99] ${
            canContinue ? 'bg-[#0066FF] text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          <span>{t.continue}</span>
        </button>
      </footer>
    </div>
  );
};

export default ClimateStep;
